import React, { useEffect, useMemo, useState } from "react";
import {
    Scale,
    Gavel,
    Plus,
    Trash2,
    UploadCloud,
    FileText,
    Bot,
    AlertTriangle,
    CheckCircle2,
    Clock,
    Loader2,
    Download,
    ChevronLeft,
    Building2,
    Calendar as CalendarIcon,
    Sparkles,
    BookOpen,
    Shield,
    AlertCircle,
    PenLine,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
    listLegalCases,
    createLegalCase,
    getLegalCase,
    deleteLegalCase,
    uploadLegalDoc,
    deleteLegalDoc,
    analyzeLegalCase,
    draftLegalRecurso,
    legalDraftDownloadUrl,
} from "@/lib/api";

const DOC_KINDS = [
    { value: "adjudicacion", label: "Resolución adjudicación" },
    { value: "pliego", label: "Pliego (PCAP / PPT)" },
    { value: "acta", label: "Acta mesa de contratación" },
    { value: "anuncio", label: "Anuncio de licitación" },
    { value: "oferta_adjudicatario", label: "Oferta adjudicatario" },
    { value: "notificacion", label: "Notificación / requerimiento" },
    { value: "otro", label: "Otro" },
];

export default function Legal() {
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState(null);
    const [showCreate, setShowCreate] = useState(false);

    useEffect(() => {
        document.title = "Auxiliar Legal · LicitaIA";
        load();
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const d = await listLegalCases();
            setCases(d.cases || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudieron cargar los casos");
        } finally {
            setLoading(false);
        }
    };

    if (selectedId) {
        return (
            <LegalCaseDetail
                caseId={selectedId}
                onBack={() => {
                    setSelectedId(null);
                    load();
                }}
                onDeleted={() => {
                    setSelectedId(null);
                    load();
                }}
            />
        );
    }

    return (
        <div className="p-6 md:p-8 space-y-6" data-testid="legal-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                    <div className="text-xs font-bold uppercase tracking-[0.18em] text-brand-600 mb-1">
                        Auxiliar Legal IA
                    </div>
                    <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-ink-950 flex items-center gap-3">
                        <Scale size={28} className="text-brand-600" />
                        Recursos en contratación
                    </h1>
                    <p className="text-ink-500 text-sm mt-1 max-w-2xl">
                        Sube los documentos del contrato y la IA evalúa si procede recurso
                        especial, de reposición, alzada o contencioso. Si procede, te lo
                        redacta listo para revisar.
                    </p>
                </div>
                <Button
                    onClick={() => setShowCreate(true)}
                    className="rounded-full bg-brand-600 hover:bg-brand-700 text-white px-6 h-11 font-bold shadow-pop"
                    data-testid="legal-new-case-btn"
                >
                    <Plus size={16} className="mr-2" />
                    Nuevo caso
                </Button>
            </div>

            {/* Disclaimer */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
                <AlertTriangle size={18} className="text-amber-700 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">
                    <div className="font-bold mb-1">Asistencia, no asesoramiento jurídico</div>
                    <div className="text-amber-800 text-xs leading-relaxed">
                        El análisis y los borradores que genera esta herramienta son
                        ASISTENCIA TÉCNICA y NO sustituyen al criterio de un letrado
                        colegiado. Antes de interponer cualquier recurso, REVISA con un
                        abogado/a con experiencia en contratación pública. La IA no
                        responde de los plazos preclusivos.
                    </div>
                </div>
            </div>

            {/* Cases list */}
            <div className="bg-white rounded-3xl border border-ink-100 shadow-pop overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-ink-400">
                        <Loader2 size={28} className="animate-spin mx-auto mb-3" />
                        Cargando casos…
                    </div>
                ) : cases.length === 0 ? (
                    <div className="p-12 text-center">
                        <Gavel size={36} className="mx-auto text-ink-300 mb-3" />
                        <div className="font-display font-bold text-ink-700">
                            Aún no hay casos abiertos
                        </div>
                        <p className="text-sm text-ink-500 mt-1 mb-5">
                            Crea uno para subir la resolución y que la IA analice si
                            procede recurso.
                        </p>
                        <Button
                            onClick={() => setShowCreate(true)}
                            className="rounded-full bg-brand-600 hover:bg-brand-700 text-white px-6 h-11 font-bold"
                            data-testid="legal-empty-create-btn"
                        >
                            <Plus size={16} className="mr-2" />
                            Crear el primero
                        </Button>
                    </div>
                ) : (
                    <div className="divide-y divide-ink-100">
                        {cases.map((c) => (
                            <button
                                key={c.id}
                                type="button"
                                onClick={() => setSelectedId(c.id)}
                                className="w-full text-left p-5 hover:bg-brand-50/30 transition-colors flex items-center gap-4"
                                data-testid={`legal-case-${c.id}`}
                            >
                                <CaseStatusIcon status={c.status} procede={c.analysis?.procede_recurso} />
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-ink-950 truncate">{c.title}</div>
                                    <div className="text-xs text-ink-500 mt-0.5 flex gap-3 flex-wrap">
                                        {c.contract_reference && (
                                            <span>
                                                <span className="font-mono">{c.contract_reference}</span>
                                            </span>
                                        )}
                                        {c.contracting_authority && (
                                            <span className="truncate max-w-[280px]">
                                                {c.contracting_authority}
                                            </span>
                                        )}
                                        <span>
                                            <Clock size={11} className="inline mr-0.5" />
                                            {formatDate(c.created_at)}
                                        </span>
                                        <span>
                                            <FileText size={11} className="inline mr-0.5" />
                                            {(c.documents || []).length} docs
                                        </span>
                                    </div>
                                </div>
                                <CaseStatusBadge status={c.status} procede={c.analysis?.procede_recurso} />
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {showCreate && (
                <CreateCaseDialog
                    onClose={() => setShowCreate(false)}
                    onCreated={(id) => {
                        setShowCreate(false);
                        setSelectedId(id);
                    }}
                />
            )}
        </div>
    );
}

// ---------- Case detail ----------

function LegalCaseDetail({ caseId, onBack, onDeleted }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");
    const [uploadKind, setUploadKind] = useState("adjudicacion");
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        load();
    }, [caseId]);

    const load = async () => {
        setLoading(true);
        try {
            const d = await getLegalCase(caseId);
            setData(d);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error cargando caso");
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy("upload");
        try {
            await uploadLegalDoc(caseId, file, uploadKind);
            toast.success("Documento subido — texto extraído");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error al subir");
        } finally {
            setBusy("");
            e.target.value = "";
        }
    };

    const handleDeleteDoc = async (docId) => {
        try {
            await deleteLegalDoc(caseId, docId);
            toast.success("Documento eliminado");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const handleAnalyze = async () => {
        if (!(data?.documents?.length > 0)) {
            toast.error("Sube al menos un documento");
            return;
        }
        setBusy("analyze");
        try {
            await analyzeLegalCase(caseId);
            toast.success("Análisis completado");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error en análisis IA");
        } finally {
            setBusy("");
        }
    };

    const handleDraft = async () => {
        setBusy("draft");
        try {
            await draftLegalRecurso(caseId);
            toast.success("Recurso redactado");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error redactando");
        } finally {
            setBusy("");
        }
    };

    const handleDelete = async () => {
        try {
            await deleteLegalCase(caseId);
            toast.success("Caso eliminado");
            onDeleted();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    if (loading || !data) {
        return (
            <div className="p-12 text-center text-ink-400">
                <Loader2 size={28} className="animate-spin mx-auto mb-3" />
                Cargando caso…
            </div>
        );
    }

    const a = data.analysis;
    const hasDraft = !!data.draft_content;

    return (
        <div className="p-6 md:p-8 space-y-6 max-w-6xl mx-auto" data-testid="legal-case-detail">
            <button
                type="button"
                onClick={onBack}
                className="text-sm text-ink-500 hover:text-brand-600 font-semibold flex items-center gap-1"
                data-testid="legal-back-btn"
            >
                <ChevronLeft size={14} />
                Volver a la lista
            </button>

            {/* Title */}
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                    <div className="text-xs font-bold uppercase tracking-[0.18em] text-brand-600 mb-1">
                        Caso · {data.contract_reference || "sin referencia"}
                    </div>
                    <h1 className="font-display text-2xl md:text-3xl font-black tracking-tight text-ink-950">
                        {data.title}
                    </h1>
                    {data.contracting_authority && (
                        <div className="text-sm text-ink-500 mt-1 flex items-center gap-1">
                            <Building2 size={13} />
                            {data.contracting_authority}
                        </div>
                    )}
                </div>
                <Button
                    onClick={() => setConfirmDelete(true)}
                    variant="outline"
                    className="rounded-full border-red-200 text-red-700 hover:bg-red-50 h-10"
                    data-testid="legal-delete-case-btn"
                >
                    <Trash2 size={14} className="mr-2" />
                    Eliminar caso
                </Button>
            </div>

            {/* Documents */}
            <Card title="1. Documentos del expediente" icon={FileText}>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <select
                        value={uploadKind}
                        onChange={(e) => setUploadKind(e.target.value)}
                        className="rounded-lg border border-ink-200 px-3 h-10 text-sm bg-white"
                        data-testid="legal-doc-kind"
                    >
                        {DOC_KINDS.map((k) => (
                            <option key={k.value} value={k.value}>
                                {k.label}
                            </option>
                        ))}
                    </select>
                    <label
                        className={`flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white rounded-full px-4 h-10 font-bold text-sm cursor-pointer transition-colors ${
                            busy === "upload" ? "opacity-60 cursor-wait" : ""
                        }`}
                        data-testid="legal-upload-trigger"
                    >
                        {busy === "upload" ? (
                            <Loader2 size={14} className="animate-spin" />
                        ) : (
                            <UploadCloud size={14} />
                        )}
                        Subir documento (PDF)
                        <input
                            type="file"
                            accept=".pdf,.txt"
                            onChange={handleUpload}
                            disabled={busy === "upload"}
                            className="hidden"
                            data-testid="legal-upload-input"
                        />
                    </label>
                </div>
                {(data.documents || []).length === 0 ? (
                    <div className="text-sm text-ink-400 italic">
                        Aún no hay documentos. Empieza por la resolución de adjudicación o el
                        acto que quieras recurrir.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {data.documents.map((d) => (
                            <div
                                key={d.id}
                                className="flex items-center gap-3 bg-slate-50 rounded-xl px-3 py-2.5 border border-ink-100"
                                data-testid={`legal-doc-${d.id}`}
                            >
                                <FileText size={15} className="text-brand-600 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-ink-950 text-sm truncate">
                                        {d.filename}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-wider text-ink-400 font-bold">
                                        {DOC_KINDS.find((x) => x.value === d.kind)?.label ||
                                            d.kind}
                                        {" · "}
                                        {d.extracted_chars
                                            ? `${d.extracted_chars.toLocaleString("es-ES")} caracteres`
                                            : "sin texto OCR"}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleDeleteDoc(d.id)}
                                    className="text-ink-400 hover:text-red-600 transition-colors"
                                    data-testid={`legal-del-doc-${d.id}`}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* Analyze */}
            <Card
                title="2. Análisis IA de viabilidad"
                icon={Bot}
                cta={
                    <Button
                        onClick={handleAnalyze}
                        disabled={busy === "analyze" || (data.documents || []).length === 0}
                        className="rounded-full bg-brand-600 hover:bg-brand-700 text-white h-9 px-4 text-sm font-bold disabled:opacity-50"
                        data-testid="legal-analyze-btn"
                    >
                        {busy === "analyze" ? (
                            <>
                                <Loader2 size={13} className="animate-spin mr-2" />
                                Analizando…
                            </>
                        ) : (
                            <>
                                <Sparkles size={13} className="mr-2" />
                                {a ? "Re-analizar" : "Analizar viabilidad"}
                            </>
                        )}
                    </Button>
                }
            >
                {!a ? (
                    <div className="text-sm text-ink-400 italic">
                        Cuando hayas subido los documentos clave (resolución de adjudicación
                        y, si los tienes, pliego y acta de la mesa) lanza el análisis IA.
                    </div>
                ) : (
                    <AnalysisBlock analysis={a} />
                )}
            </Card>

            {/* Draft */}
            <Card
                title="3. Borrador del recurso"
                icon={PenLine}
                cta={
                    a?.procede_recurso ? (
                        <Button
                            onClick={handleDraft}
                            disabled={busy === "draft"}
                            className="rounded-full bg-brand-600 hover:bg-brand-700 text-white h-9 px-4 text-sm font-bold disabled:opacity-50"
                            data-testid="legal-draft-btn"
                        >
                            {busy === "draft" ? (
                                <>
                                    <Loader2 size={13} className="animate-spin mr-2" />
                                    Redactando…
                                </>
                            ) : (
                                <>
                                    <PenLine size={13} className="mr-2" />
                                    {hasDraft ? "Re-redactar" : "Redactar recurso"}
                                </>
                            )}
                        </Button>
                    ) : null
                }
            >
                {!a ? (
                    <div className="text-sm text-ink-400 italic">
                        Lanza primero el análisis IA.
                    </div>
                ) : !a.procede_recurso ? (
                    <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-900">
                        El análisis indica que NO procede recurso. No se redacta el borrador.
                        Revisa la valoración estratégica arriba.
                    </div>
                ) : !hasDraft ? (
                    <div className="text-sm text-ink-400 italic">
                        Pulsa "Redactar recurso" para que la IA componga el escrito formal
                        según la estructura de TACRC.
                    </div>
                ) : (
                    <>
                        <div className="flex gap-2 mb-4">
                            <a
                                href={legalDraftDownloadUrl(caseId, "docx")}
                                target="_blank"
                                rel="noreferrer"
                                className="bg-brand-600 hover:bg-brand-700 text-white rounded-full px-4 h-9 font-bold text-sm inline-flex items-center transition-colors"
                                data-testid="legal-download-docx"
                            >
                                <Download size={13} className="mr-2" />
                                Descargar DOCX
                            </a>
                            <a
                                href={legalDraftDownloadUrl(caseId, "pdf")}
                                target="_blank"
                                rel="noreferrer"
                                className="bg-white border border-ink-200 hover:border-brand-300 rounded-full px-4 h-9 font-bold text-sm inline-flex items-center transition-colors text-ink-800"
                                data-testid="legal-download-pdf"
                            >
                                <Download size={13} className="mr-2" />
                                Descargar PDF
                            </a>
                        </div>
                        <pre
                            className="bg-slate-50 border border-ink-100 rounded-xl p-4 text-xs whitespace-pre-wrap text-ink-800 font-serif leading-relaxed max-h-[600px] overflow-auto"
                            data-testid="legal-draft-preview"
                        >
                            {data.draft_content}
                        </pre>
                    </>
                )}
            </Card>

            {confirmDelete && (
                <Dialog open onOpenChange={(o) => !o && setConfirmDelete(false)}>
                    <DialogContent className="max-w-md rounded-3xl">
                        <DialogHeader>
                            <DialogTitle className="font-display font-black">
                                Eliminar este caso
                            </DialogTitle>
                            <DialogDescription>
                                Se borrarán todos los documentos, el análisis y el borrador del
                                recurso. Esta acción es irreversible.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter className="gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setConfirmDelete(false)}
                                className="rounded-full"
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleDelete}
                                className="rounded-full bg-red-600 hover:bg-red-700 text-white font-bold"
                                data-testid="legal-confirm-delete"
                            >
                                <Trash2 size={14} className="mr-2" />
                                Eliminar
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}

// ---------- Sub-components ----------

function Card({ title, icon: Icon, cta, children }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-white rounded-3xl border border-ink-100 shadow-pop overflow-hidden"
        >
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-ink-100 bg-slate-50/50">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center">
                        <Icon size={16} />
                    </div>
                    <div className="font-display text-lg font-bold text-ink-950">{title}</div>
                </div>
                {cta}
            </div>
            <div className="p-5">{children}</div>
        </motion.div>
    );
}

function AnalysisBlock({ analysis: a }) {
    return (
        <div className="space-y-5" data-testid="legal-analysis-block">
            {/* Verdict */}
            <div
                className={`rounded-2xl p-5 border-2 ${
                    a.procede_recurso
                        ? "bg-emerald-50 border-emerald-300"
                        : "bg-rose-50 border-rose-300"
                }`}
            >
                <div className="flex items-start gap-4">
                    <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-pop ${
                            a.procede_recurso
                                ? "bg-gradient-to-br from-emerald-500 to-cyan-500"
                                : "bg-gradient-to-br from-rose-500 to-orange-500"
                        }`}
                    >
                        {a.procede_recurso ? (
                            <CheckCircle2 size={22} />
                        ) : (
                            <AlertCircle size={22} />
                        )}
                    </div>
                    <div className="flex-1">
                        <div className="text-xs font-bold uppercase tracking-[0.18em] mb-1 opacity-70">
                            Veredicto IA · Confianza {a.confidence_score}%
                        </div>
                        <div className="font-display text-2xl font-black leading-tight">
                            {a.procede_recurso ? "SÍ procede recurso" : "NO procede recurso"}
                        </div>
                        <div className="text-sm mt-2 opacity-80">
                            {a.acto_recurrible || "Acto recurrido"}
                        </div>
                    </div>
                </div>
            </div>

            {/* Key facts */}
            {a.procede_recurso && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KpiBox label="Tipo de recurso" value={tipoLabel(a.tipo_recurso)} />
                    <KpiBox label="Órgano competente" value={a.organo_competente || "—"} />
                    <KpiBox
                        label="Plazo legal"
                        value={
                            a.plazo_dias_habiles
                                ? `${a.plazo_dias_habiles} días hábiles`
                                : "—"
                        }
                    />
                    <KpiBox
                        label="Fecha límite"
                        value={a.fecha_limite || "Por calcular"}
                        highlight={!!a.fecha_limite}
                    />
                </div>
            )}

            {/* Strategic assessment */}
            {a.valoracion_estrategica && (
                <div>
                    <SectionTitle icon={Sparkles}>Valoración estratégica</SectionTitle>
                    <p className="text-sm text-ink-700 leading-relaxed bg-slate-50 rounded-xl p-4 whitespace-pre-wrap">
                        {a.valoracion_estrategica}
                    </p>
                </div>
            )}

            {/* Motivos */}
            {(a.motivos || []).length > 0 && (
                <div>
                    <SectionTitle icon={Gavel}>
                        Motivos jurídicos ({a.motivos.length})
                    </SectionTitle>
                    <div className="space-y-2">
                        {a.motivos.map((m, i) => (
                            <div
                                key={i}
                                className="border-l-4 border-brand-500 bg-brand-50/40 rounded-r-xl p-4"
                                data-testid={`legal-motivo-${i}`}
                            >
                                <div className="font-bold text-ink-950 text-sm">{m.titulo}</div>
                                {m.fundamento_legal && (
                                    <div className="text-xs font-mono text-brand-700 mt-1">
                                        {m.fundamento_legal}
                                    </div>
                                )}
                                {m.descripcion && (
                                    <div className="text-sm text-ink-700 mt-2 leading-relaxed">
                                        {m.descripcion}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Doctrine */}
            {(a.doctrina_aplicable || []).length > 0 && (
                <div>
                    <SectionTitle icon={BookOpen}>Doctrina aplicable</SectionTitle>
                    <div className="space-y-2">
                        {a.doctrina_aplicable.map((d, i) => (
                            <div
                                key={i}
                                className="bg-slate-50 rounded-xl p-3 border border-ink-100"
                            >
                                <div className="text-xs font-mono font-bold text-brand-700">
                                    {d.tribunal} · {d.resolucion}
                                </div>
                                <div className="text-sm text-ink-700 mt-1">{d.doctrina}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Cautelar */}
            {a.medidas_cautelares && (
                <div>
                    <SectionTitle icon={Shield}>Medidas cautelares</SectionTitle>
                    <div className="text-sm text-ink-700 leading-relaxed bg-amber-50/50 border border-amber-200 rounded-xl p-4 whitespace-pre-wrap">
                        {a.medidas_cautelares}
                    </div>
                </div>
            )}

            {/* Suplico */}
            {(a.suplico || []).length > 0 && (
                <div>
                    <SectionTitle icon={Scale}>Suplico</SectionTitle>
                    <ol className="list-decimal list-inside space-y-1 text-sm text-ink-700">
                        {a.suplico.map((s, i) => (
                            <li key={i} className="leading-relaxed">
                                {s}
                            </li>
                        ))}
                    </ol>
                </div>
            )}

            {/* Disclaimer */}
            {a.advertencia && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 flex items-start gap-2">
                    <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{a.advertencia}</span>
                </div>
            )}
        </div>
    );
}

function KpiBox({ label, value, highlight }) {
    return (
        <div
            className={`rounded-xl p-3 border ${
                highlight
                    ? "bg-brand-50 border-brand-200"
                    : "bg-white border-ink-100"
            }`}
        >
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-400">
                {label}
            </div>
            <div
                className={`font-mono text-sm font-bold mt-1 ${
                    highlight ? "text-brand-700" : "text-ink-950"
                }`}
            >
                {value}
            </div>
        </div>
    );
}

function SectionTitle({ icon: Icon, children }) {
    return (
        <div className="flex items-center gap-2 mb-3">
            <Icon size={14} className="text-brand-600" />
            <div className="font-display font-bold text-sm uppercase tracking-[0.14em] text-ink-700">
                {children}
            </div>
        </div>
    );
}

function CaseStatusIcon({ status, procede }) {
    if (status === "drafted")
        return (
            <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                <PenLine size={16} />
            </div>
        );
    if (status === "analyzed")
        return (
            <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    procede
                        ? "bg-brand-100 text-brand-700"
                        : "bg-rose-100 text-rose-700"
                }`}
            >
                {procede ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            </div>
        );
    if (status === "documents_uploaded")
        return (
            <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                <FileText size={16} />
            </div>
        );
    return (
        <div className="w-10 h-10 rounded-xl bg-ink-100 text-ink-500 flex items-center justify-center shrink-0">
            <Scale size={16} />
        </div>
    );
}

function CaseStatusBadge({ status, procede }) {
    const map = {
        draft: { text: "Sin documentos", color: "bg-ink-100 text-ink-600" },
        documents_uploaded: {
            text: "Sin analizar",
            color: "bg-amber-100 text-amber-700",
        },
        analyzed: procede
            ? { text: "Procede recurso", color: "bg-emerald-100 text-emerald-700" }
            : { text: "No procede", color: "bg-rose-100 text-rose-700" },
        drafted: { text: "Borrador listo", color: "bg-brand-100 text-brand-700" },
    };
    const cur = map[status] || map.draft;
    return (
        <span
            className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${cur.color}`}
        >
            {cur.text}
        </span>
    );
}

function CreateCaseDialog({ onClose, onCreated }) {
    const [form, setForm] = useState({
        title: "",
        contract_reference: "",
        contracting_authority: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) {
            toast.error("Pon un título al caso");
            return;
        }
        setSubmitting(true);
        try {
            const r = await createLegalCase(form);
            toast.success("Caso creado");
            onCreated(r.id);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-xl rounded-3xl" data-testid="legal-create-dialog">
                <DialogHeader>
                    <div className="flex items-center gap-3 mb-1">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-brand-600 to-cyan-500 text-white flex items-center justify-center shadow-pop">
                            <Gavel size={18} />
                        </div>
                        <div>
                            <DialogTitle className="font-display text-2xl font-black">
                                Nuevo caso legal
                            </DialogTitle>
                            <DialogDescription className="text-sm">
                                Identifica el contrato y el órgano. Después subes los
                                documentos.
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 mt-2">
                    <div>
                        <Label>Título del caso *</Label>
                        <Input
                            value={form.title}
                            onChange={update("title")}
                            placeholder="Recurso adjudicación servicio vigilancia SEPE"
                            data-testid="legal-new-title"
                        />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <Label>Nº de expediente</Label>
                            <Input
                                value={form.contract_reference}
                                onChange={update("contract_reference")}
                                placeholder="CO-2026-007"
                                className="font-mono"
                                data-testid="legal-new-reference"
                            />
                        </div>
                        <div>
                            <Label>Órgano de contratación</Label>
                            <Input
                                value={form.contracting_authority}
                                onChange={update("contracting_authority")}
                                placeholder="Diputación Provincial de Sevilla"
                                data-testid="legal-new-authority"
                            />
                        </div>
                    </div>
                    <DialogFooter className="gap-2 mt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            className="rounded-full"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="rounded-full bg-brand-600 hover:bg-brand-700 text-white font-bold"
                            data-testid="legal-submit-create"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 size={14} className="animate-spin mr-2" />
                                    Creando…
                                </>
                            ) : (
                                <>
                                    <Plus size={14} className="mr-2" />
                                    Crear caso
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function Label({ children }) {
    return (
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 mb-1.5">
            {children}
        </div>
    );
}

function tipoLabel(t) {
    return (
        {
            especial: "Especial contratación",
            reposicion: "Reposición",
            alzada: "Alzada",
            contencioso: "Contencioso-admin.",
            no_procede: "No procede",
        }[t] || t || "—"
    );
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return "—";
    }
}
